import { UPLOAD_RULES, checkUploadFile, uploadAccept, uploadHint } from "utils/uploadRules";
import React, { memo, useState, useCallback, useRef, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import {
  Row,
  Col,
  Checkbox,
  Tooltip,
  Typography,
  Input,
  Switch,
  Form,
  DatePicker,
  Radio,
  Spin,
} from "antd";
import {
  CheckOutlined,
  ClearOutlined,
  TeamOutlined,
  ScheduleOutlined,
  ExperimentOutlined,
  SettingOutlined,
  BarChartOutlined,
  UserOutlined,
  LockOutlined,
  MailOutlined,
  UploadOutlined,
  FileOutlined,
  CloseCircleOutlined,
  ApartmentOutlined,
  MessageOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { getPreSignedUrl } from "utils/MyMUtils";
import { PASSWORD_HINT, PASSWORD_MAX, PASSWORD_MESSAGES, confirmPasswordRules, passwordProblem, passwordRules } from "utils/passwordRules";

const { Text } = Typography;

// ─── Brand color ─────────────────────────────────────────────────────────────

export const BRAND = "#2f6fe0";

// ─── Steps ───────────────────────────────────────────────────────────────────

export const STEPS = [
  { num: 1, label: "Identity" },
  { num: 2, label: "Role & Access" },
  { num: 3, label: "Facility" },
  { num: 4, label: "Login" },
  { num: 5, label: "Compliance" },
];

// ─── Initial state shapes ─────────────────────────────────────────────────────

export const INITIAL_IDENTITY = {
  firstName: "",
  lastName: "",
  mobileNo: "",
  isAgencyStaff: false,
};

export const INITIAL_LOGIN = {
  username: "",
  resetPassword: false,
  password: "",
  email: "",
};

// ─── Compliance ───────────────────────────────────────────────────────────────

export const COMPLIANCE_ITEMS = [
  { key: "flu", label: "Flu", sub: "Valid / Expiring / Missing" },
  { key: "covid", label: "COVID", sub: "Valid / Expiring / Missing" },
  { key: "stat", label: "Stat Declaration", sub: "Uploaded / Missing" },
  { key: "police", label: "Police Check", sub: "Reference / Expiry / Missing" },
];

export const INITIAL_COMPLIANCE = {
  flu: { file: null, vaccinationDate: null },
  covid: { file: null, vaccinationDate: null },
  stat: { file: null },
  police: {
    file: null,
    referenceNumber: "",
    certificateSighted: null,
    offences: null,
    issueDate: null,
  },
};

export const COMPLIANCE_ALLOWED_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
]);
export const COMPLIANCE_ALLOWED_EXTENSIONS = /\.(pdf|jpe?g|png)$/i;
export const COMPLIANCE_MAX_SIZE_MB = UPLOAD_RULES.STAFF_DOCUMENT.maxMB;
export const COMPLIANCE_MAX_SIZE_BYTES = COMPLIANCE_MAX_SIZE_MB * 1024 * 1024;

// ─── Fixed module list (canonical modules shown in facility assignment) ────────
// Matches userManagement/Step3.js and existingStep2.js:
// excludes Facility Portal (21), Facility Management (23), Communication (24)

export const FIXED_MODULES = [
  { module_id: 18, name: "Cleaning Audit" },
  { module_id: 24, name: "Communication" },
  { module_id: 20, name: "Employee Connect" },
  { module_id: 19, name: "Laundry Audit" },
  { module_id: 2,  name: "Work Scheduler" },
];

export const ALLOWED_MODULE_IDS = new Set(FIXED_MODULES.map((m) => m.module_id));

/**
 * Given a list of modules from the API, filter to only the allowed 7
 * and return them in the canonical FIXED_MODULES order.
 */
export const filterAllowedModules = (apiModules = []) => {
  const apiMap = new Map(apiModules.map((m) => [m.module_id ?? m.id, m]));
  return FIXED_MODULES.filter((fm) => apiMap.has(fm.module_id)).map((fm) => ({
    ...fm,
    ...apiMap.get(fm.module_id),
    // always use canonical name (handles both name and module_name from API)
    name: fm.name,
    module_id: fm.module_id,
  }));
};

// ─── Module icon map ──────────────────────────────────────────────────────────

export const MODULE_ICON_MAP = {
  "cleaning audit":   ClearOutlined,
  "employee connect": TeamOutlined,
  "laundry audit":    ExperimentOutlined,
  "work scheduler":   ScheduleOutlined,
  // kept as fallbacks
  "communication":        MessageOutlined,
  "facility management":  ApartmentOutlined,
  "facility portal":      HomeOutlined,
  admin:                  SettingOutlined,
  reports:                BarChartOutlined,
};

export const getModuleIcon = (name = "") =>
  MODULE_ICON_MAP[name.toLowerCase().trim()] || SettingOutlined;

// ─── Validators ───────────────────────────────────────────────────────────────

const ALPHA_RE = /^[a-zA-Z]+$/;
const MOBILE_RE = /^\d{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Login details follow PureVue's rules, the same as Manage Client's users:
// usernames like "sam.a", passwords like "Sam-Test-1" (8 or more, no spaces).
const USERNAME_RE = /^[a-zA-Z0-9._-]+$/;
const USERNAME_MAX = 50;
const USERNAME_MESSAGE = "Use letters, numbers, dot (.), underscore (_) or hyphen (-) only.";

export const validateName = (value) => {
  if (!value.trim()) return "This field is required.";
  if (!ALPHA_RE.test(value)) return "Only alphabetic characters are allowed.";
  if (value.length > 50) return "Maximum 50 characters allowed.";
  return null;
};

export const validateMobile = (value) => {
  if (!value.trim()) return "This field is required.";
  const stripped = value.replace(/\s/g, "");
  if (!/^\d+$/.test(stripped)) return "Only numeric characters are allowed.";
  if (!MOBILE_RE.test(stripped))
    return "Mobile number must be exactly 10 digits.";
  return null;
};

export const validateUsername = (value) => {
  if (!value.trim()) return "Username is required.";
  if (!USERNAME_RE.test(value)) return USERNAME_MESSAGE;
  if (value.length > USERNAME_MAX) return `Maximum ${USERNAME_MAX} characters allowed.`;
  return null;
};

// the standard password rule (utils/passwordRules)
export const validatePassword = (value) => passwordProblem(value);

// the password and its confirmation on the Login step
export const validateLoginPassword = (login) =>
  passwordProblem(login.password) || (login.password !== (login.confirm_password || "") ? PASSWORD_MESSAGES.mismatch : null);

export const validateEmail = (value) => {
  if (!value.trim()) return "Email is required.";
  if (!EMAIL_RE.test(value)) return "Enter a valid email address.";
  return null;
};

// ─── Form normalisers / rules ─────────────────────────────────────────────────

export const USERNAME_NORMALIZE = (v) =>
  v.replace(/[^a-zA-Z0-9._-]/g, "").slice(0, USERNAME_MAX);

export const PASSWORD_NORMALIZE = (v) => v.replace(/\s/g, "").slice(0, PASSWORD_MAX);

export const USERNAME_RULES = [{ pattern: USERNAME_RE, message: USERNAME_MESSAGE }];

export const PASSWORD_RULES = passwordRules(false);

// ─── Shared primitives ────────────────────────────────────────────────────────

export const SectionLabel = memo(({ children }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      color: "#5B6472",
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      marginBottom: 6,
    }}
  >
    {children}
  </div>
));

// ─── Step indicator ───────────────────────────────────────────────────────────

// steps: [{ num, label }], the staff wizard's by default
export const StepIndicator = memo(({ currentStep, steps = STEPS }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: 16,
      overflowX: "auto",
      paddingBottom: 4,
    }}
  >
    {steps.map((step, idx) => {
      const isCompleted = step.num < currentStep;
      const isActive = step.num === currentStep;

      return (
        <React.Fragment key={step.num}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 5,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                border: `2px solid ${
                  isCompleted || isActive ? BRAND : "#c8d6e5"
                }`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700,
                color: isCompleted || isActive ? "#fff" : "#5B6472",
                background: isCompleted || isActive ? BRAND : "#fff",
                flexShrink: 0,
                transition: "all 0.25s ease",
              }}
            >
              {isCompleted ? (
                <CheckOutlined style={{ fontSize: 11 }} />
              ) : (
                step.num
              )}
            </div>
            <span
              style={{
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
                color: isCompleted || isActive ? BRAND : "#5B6472",
                whiteSpace: "nowrap",
                transition: "all 0.25s ease",
              }}
            >
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <div
              style={{
                flex: 1,
                height: 2,
                background: step.num < currentStep ? BRAND : "#d0dde9",
                margin: "0 8px",
                marginBottom: 18,
                minWidth: 16,
                borderRadius: 1,
                transition: "background 0.3s ease",
              }}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
));

// ─── Role step primitives ─────────────────────────────────────────────────────

export const RoleCard = memo(({ role, selected, onSelect }) => (
  <div
    onClick={onSelect}
    style={{
      border: `1.5px solid ${selected ? BRAND : "#d9e3ee"}`,
      borderRadius: 6,
      padding: "12px 14px",
      cursor: "pointer",
      background: selected ? "#f0f5fb" : "#fff",
      transition: "all 0.18s ease",
    }}
  >
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: selected ? BRAND : "#101828",
        textAlign: "center",
      }}
    >
      {role.title}
    </div>
    <div style={{ fontSize: 12, color: "#5B6472" }}>{role.desc}</div>
  </div>
));

// ─── Facility step primitives ─────────────────────────────────────────────────

export const ModuleIconChip = memo(
  ({ mod, selected, facilitySelected, facilityHasModule, onToggle }) => {
    const Icon = getModuleIcon(mod.name);
    const isInteractive = facilitySelected && facilityHasModule;
    return (
      <Tooltip title={mod.name} placement="top" mouseEnterDelay={0.2}>
        <div
          onClick={isInteractive ? onToggle : undefined}
          style={{
            width: 34,
            height: 34,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1.5px solid ${
              isInteractive && selected
                ? BRAND
                : isInteractive
                ? "#d0dde9"
                : "#edf2f9"
            }`,
            borderRadius: 6,
            cursor: isInteractive ? "pointer" : "default",
            background:
              isInteractive && selected ? "#e8f0fb" : "#fafbfc",
            transition: "all 0.15s ease",
            flexShrink: 0,
          }}
        >
          <Icon
            style={{
              fontSize: 15,
              color:
                isInteractive && selected
                  ? BRAND
                  : isInteractive
                  ? "#a0b0c4"
                  : "#d0dde9",
              transition: "color 0.15s ease",
            }}
          />
        </div>
      </Tooltip>
    );
  }
);

export const FacilityRow = memo(
  ({
    facility,
    facilitySelected,
    facilityMods,
    allModules,
    onToggle,
    onToggleModule,
  }) => {
    const facilityModuleIds = useMemo(
      () => new Set((facility.modules || []).map((m) => m.module_id)),
      [facility.modules]
    );

    const enabledCount = facilitySelected
      ? Object.values(facilityMods).filter(Boolean).length
      : 0;

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          padding: "8px 12px",
          border: `1px solid ${facilitySelected ? BRAND : "#edf2f9"}`,
          borderRadius: 7,
          marginBottom: 8,
          background: facilitySelected ? "#f4f8fd" : "#fafbfc",
          transition: "all 0.18s ease",
        }}
      >
        <Checkbox
          checked={facilitySelected}
          onChange={onToggle}
          style={{ flexShrink: 0, marginRight: 10 }}
        />

        <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: facilitySelected ? 600 : 400,
              color: facilitySelected ? BRAND : "#5a6a7e",
              transition: "color 0.15s ease",
            }}
          >
            {facility.facility_name}
          </div>
          {facilitySelected && (
            <div style={{ fontSize: 10, color: "#5B6472", marginTop: 1 }}>
              {enabledCount}/{facility.modules.length} on
            </div>
          )}
        </div>

        <div
          style={{
            width: 1,
            height: 30,
            background: facilitySelected ? "#c8daf0" : "#edf2f9",
            flexShrink: 0,
            marginRight: 12,
            transition: "background 0.15s ease",
          }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          {allModules.map((mod) => (
            <ModuleIconChip
              key={mod.module_id}
              mod={mod}
              selected={facilityMods[mod.module_id] || false}
              facilitySelected={facilitySelected}
              facilityHasModule={facilityModuleIds.has(mod.module_id)}
              onToggle={() => onToggleModule(mod.module_id)}
            />
          ))}
        </div>
      </div>
    );
  }
);

// ─── Compliance step primitives ───────────────────────────────────────────────

export const ComplianceNavItem = memo(({ item, isActive, status, onClick }) => {
  const ssMap = {
    done: {
      bg: "#f6ffed",
      border: "#95de64",
      iconColor: "#1A8F5C",
      sub: "Complete",
    },
    pending: {
      bg: "#fff7e6",
      border: "#ffd591",
      iconColor: "#d48806",
      sub: "Fields missing",
    },
    none: {
      bg: "#f5f7fa",
      border: "#d9e3ee",
      iconColor: "#a0b0c4",
      sub: "Optional",
    },
  };
  const ss = ssMap[status] || ssMap.none;

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px",
        borderRadius: 7,
        cursor: "pointer",
        background: isActive ? "#f0f5fb" : "#fafbfc",
        border: `1.5px solid ${isActive ? BRAND : "#edf2f9"}`,
        transition: "all 0.18s ease",
        marginBottom: 6,
        userSelect: "none",
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          background: isActive ? BRAND : ss.bg,
          border: `1.5px solid ${isActive ? BRAND : ss.border}`,
          transition: "all 0.18s ease",
        }}
      >
        {status === "done" ? (
          <CheckOutlined
            style={{ fontSize: 11, color: isActive ? "#fff" : ss.iconColor }}
          />
        ) : (
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: isActive ? "#fff" : ss.iconColor,
              lineHeight: 1,
            }}
          >
            {status === "pending" ? "!" : "–"}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? BRAND : "#3c4a5c",
            transition: "color 0.15s ease",
          }}
        >
          {item.label}
        </div>
        <div
          style={{
            fontSize: 11,
            color: isActive ? "#6890bb" : ss.iconColor,
            marginTop: 1,
            transition: "color 0.15s ease",
          }}
        >
          {ss.sub}
        </div>
      </div>
    </div>
  );
});

export const ComplianceDetailPanel = memo(
  ({
    item,
    complianceData,
    touched,
    onMarkTouched,
    onFileChange,
    onFieldChange,
  }) => {
    const inputRef = useRef(null);

    const handleFileSelect = useCallback(
      (e) => {
        const selected = e.target.files[0] || null;
        e.target.value = "";
        if (!selected) return;
        const error = checkUploadFile(selected, UPLOAD_RULES.STAFF_DOCUMENT);
        if (error) {
          toast.error(error);
          return;
        }
        onFileChange(item.key, selected);
      },
      [item.key, onFileChange]
    );

    const handleRemove = useCallback(
      (e) => {
        e.stopPropagation();
        onFileChange(item.key, null);
      },
      [item.key, onFileChange]
    );

    const file = complianceData.file;
    const hasFile = file !== null;

    const isEmptyField = (field, value) => {
      if (field === "certificateSighted" || field === "offences")
        return value === null;
      if (typeof value === "string") return !value.trim();
      return value === null || value === undefined;
    };

    const fStatus = (field, value) =>
      hasFile && touched[field] && isEmptyField(field, value) ? "error" : "";
    const fHelp = (field, value, msg) =>
      hasFile && touched[field] && isEmptyField(field, value) ? msg : "";
    const isFluCovid = item.key === "flu" || item.key === "covid";
    const isPolice = item.key === "police";

    const sectionHead = (text) => (
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: "#5B6472",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 8,
        }}
      >
        {text}
      </div>
    );

    const uploadZone = (
      <div>
        {hasFile ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              border: `1.5px solid ${BRAND}`,
              borderRadius: 7,
              padding: "10px 14px",
              background: "#f0f7ff",
            }}
          >
            <FileOutlined style={{ color: BRAND, fontSize: 16, flexShrink: 0 }} />
            <span
              style={{
                fontSize: 13,
                color: BRAND,
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {file.name}
            </span>
            {file.isExisting && file.url ? (
              <a
                onClick={(e) => {
                  e.preventDefault();
                  getPreSignedUrl(file.url);
                }}
                href={file.url}
                style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}
              >
                View
              </a>
            ) : null}
            <CloseCircleOutlined
              onClick={handleRemove}
              style={{
                color: "#a0b0c4",
                fontSize: 15,
                cursor: "pointer",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#e84a4a")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#a0b0c4")}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              width: "100%",
              padding: "12px 16px",
              border: "1.5px dashed #c8d6e5",
              borderRadius: 7,
              background: "#fff",
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = BRAND;
              e.currentTarget.style.background = "#f0f5fb";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#c8d6e5";
              e.currentTarget.style.background = "#fff";
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                background: "#f4f6f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <UploadOutlined style={{ fontSize: 16, color: "#5B6472" }} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 13,
                  color: "#3c4a5c",
                  fontWeight: 500,
                  marginBottom: 2,
                }}
              >
                Click to upload document
              </div>
              <div style={{ fontSize: 11, color: "#a0b0c4" }}>
                {uploadHint(UPLOAD_RULES.STAFF_DOCUMENT)}
              </div>
            </div>
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={uploadAccept(UPLOAD_RULES.STAFF_DOCUMENT)}
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
      </div>
    );

    if (isFluCovid) {
      return (
        <div>
          {sectionHead("Document")}
          {uploadZone}
          <div style={{ marginTop: 16 }}>
            {sectionHead("Details")}
            <Form.Item
              label="Vaccination Date"
              required={hasFile}
              validateStatus={fStatus(
                "vaccinationDate",
                complianceData.vaccinationDate
              )}
              help={fHelp(
                "vaccinationDate",
                complianceData.vaccinationDate,
                "Vaccination date is required."
              )}
              style={{ marginBottom: 0 }}
            >
              <DatePicker
                format="DD-MM-YYYY"
                style={{ width: "100%", maxWidth: 260 }}
                value={complianceData.vaccinationDate}
                disabled={!hasFile}
                disabledDate={(current) => current && current.isAfter(new Date(), "day")}
                onChange={(date) => {
                  onFieldChange(item.key, "vaccinationDate", date);
                  onMarkTouched("vaccinationDate");
                }}
                onBlur={() => onMarkTouched("vaccinationDate")}
                placeholder="Select vaccination date"
              />
            </Form.Item>
          </div>
        </div>
      );
    }

    if (isPolice) {
      return (
        <div>
          {sectionHead("Document")}
          {uploadZone}
          <div style={{ marginTop: 16 }}>
            {sectionHead("Details")}
            <Row gutter={[16, 0]}>
              <Col span={14}>
                <Form.Item
                  label="Reference Number"
                  required={hasFile}
                  validateStatus={fStatus(
                    "referenceNumber",
                    complianceData.referenceNumber
                  )}
                  help={fHelp(
                    "referenceNumber",
                    complianceData.referenceNumber,
                    "Reference number is required."
                  )}
                  style={{ marginBottom: 12 }}
                >
                  <Input
                    value={complianceData.referenceNumber}
                    disabled={!hasFile}
                    onChange={(e) =>
                      onFieldChange(
                        item.key,
                        "referenceNumber",
                        e.target.value.slice(0, 50)
                      )
                    }
                    onBlur={() => onMarkTouched("referenceNumber")}
                    placeholder="e.g. PC123456"
                    maxLength={50}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  label="Issue Date"
                  required={hasFile}
                  validateStatus={fStatus("issueDate", complianceData.issueDate)}
                  help={fHelp(
                    "issueDate",
                    complianceData.issueDate,
                    "Issue date is required."
                  )}
                  style={{ marginBottom: 12 }}
                >
                  <DatePicker
                    format="DD-MM-YYYY"
                    style={{ width: "100%" }}
                    value={complianceData.issueDate}
                    disabled={!hasFile}
                    disabledDate={(current) => current && current.isAfter(new Date(), "day")}
                    onChange={(date) => {
                      onFieldChange(item.key, "issueDate", date);
                      onMarkTouched("issueDate");
                    }}
                    onBlur={() => onMarkTouched("issueDate")}
                    placeholder="Select date"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Certificate Sighted"
                  required={hasFile}
                  validateStatus={fStatus(
                    "certificateSighted",
                    complianceData.certificateSighted
                  )}
                  help={fHelp(
                    "certificateSighted",
                    complianceData.certificateSighted,
                    "Required."
                  )}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    value={complianceData.certificateSighted}
                    disabled={!hasFile}
                    onChange={(e) => {
                      onFieldChange(
                        item.key,
                        "certificateSighted",
                        e.target.value
                      );
                      onMarkTouched("certificateSighted");
                    }}
                    style={{ display: "flex", gap: 8, width: "100%" }}
                  >
                    {[
                      { label: "Yes", val: true },
                      { label: "No", val: false },
                    ].map(({ label, val }) => {
                      const isSelected =
                        hasFile &&
                        complianceData.certificateSighted === val;
                      return (
                        <div
                          key={label}
                          style={{
                            flex: 1,
                            border: `1.5px solid ${
                              isSelected ? BRAND : "#d9e3ee"
                            }`,
                            borderRadius: 6,
                            padding: "6px 10px",
                            background: isSelected
                              ? "#f0f5fb"
                              : !hasFile
                              ? "#f5f5f5"
                              : "#fafbfc",
                            transition: "all 0.15s ease",
                            cursor: !hasFile ? "not-allowed" : "pointer",
                          }}
                        >
                          <Radio
                            value={val}
                            style={{
                              color: isSelected ? BRAND : undefined,
                              fontWeight: isSelected ? 600 : 400,
                              width: "100%",
                            }}
                          >
                            {label}
                          </Radio>
                        </div>
                      );
                    })}
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Offences"
                  required={hasFile}
                  validateStatus={fStatus("offences", complianceData.offences)}
                  help={fHelp(
                    "offences",
                    complianceData.offences,
                    "Required."
                  )}
                  style={{ marginBottom: 0 }}
                >
                  <Radio.Group
                    value={complianceData.offences}
                    disabled={!hasFile}
                    onChange={(e) => {
                      onFieldChange(item.key, "offences", e.target.value);
                      onMarkTouched("offences");
                    }}
                    style={{ display: "flex", gap: 8, width: "100%" }}
                  >
                    {[
                      { label: "Yes", val: true },
                      { label: "No", val: false },
                    ].map(({ label, val }) => {
                      const isSelected =
                        hasFile && complianceData.offences === val;
                      return (
                        <div
                          key={label}
                          style={{
                            flex: 1,
                            border: `1.5px solid ${
                              isSelected ? BRAND : "#d9e3ee"
                            }`,
                            borderRadius: 6,
                            padding: "6px 10px",
                            background: isSelected
                              ? "#f0f5fb"
                              : !hasFile
                              ? "#f5f5f5"
                              : "#fafbfc",
                            transition: "all 0.15s ease",
                            cursor: !hasFile ? "not-allowed" : "pointer",
                          }}
                        >
                          <Radio
                            value={val}
                            style={{
                              color: isSelected ? BRAND : undefined,
                              fontWeight: isSelected ? 600 : 400,
                              width: "100%",
                            }}
                          >
                            {label}
                          </Radio>
                        </div>
                      );
                    })}
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
      );
    }

    // Stat Declaration — upload only
    return (
      <div>
        {sectionHead("Document")}
        {uploadZone}
      </div>
    );
  }
);

// ─── Step panels ───────────────────────────────────────────────────────────────

export const IdentityStep = memo(({ identity, onChange }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    form.setFieldsValue({
      firstName: identity.firstName,
      lastName: identity.lastName,
      mobileNo: identity.mobileNo,
    });
  }, [identity, form]);

  return (
    <div
      style={{ border: "1px solid #edf2f9", borderRadius: 8, padding: "16px 20px" }}
    >
      <SectionLabel>Basic Details</SectionLabel>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          firstName: identity.firstName,
          lastName: identity.lastName,
          mobileNo: identity.mobileNo,
        }}
        onValuesChange={(changed) => {
          Object.keys(changed).forEach((field) =>
            onChange(field, changed[field])
          );
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              validateTrigger="onBlur"
              normalize={(v) => v.replace(/[^a-zA-Z]/g, "").slice(0, 50)}
              rules={[
                { required: true, message: "Please enter the first name." },
                {
                  pattern: /^[a-zA-Z]+$/,
                  message: "Only alphabetic characters are allowed.",
                },
              ]}
            >
              <Input placeholder="e.g. Sarah" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Last Name"
              validateTrigger="onBlur"
              normalize={(v) => v.replace(/[^a-zA-Z]/g, "").slice(0, 50)}
              rules={[
                { required: true, message: "Please enter the last name." },
                {
                  pattern: /^[a-zA-Z]+$/,
                  message: "Only alphabetic characters are allowed.",
                },
              ]}
            >
              <Input placeholder="e.g. Johnson" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="mobileNo"
          label="Mobile Number"
          validateTrigger="onBlur"
          normalize={(v) => v.replace(/[^0-9\s]/g, "")}
          rules={[
            { required: true, message: "Please enter the mobile number." },
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const stripped = value.replace(/\s/g, "");
                return /^\d{10}$/.test(stripped)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Mobile number must be exactly 10 digits.")
                    );
              },
            },
          ]}
        >
          <Input placeholder="e.g. 0412 345 678" />
        </Form.Item>
      </Form>

      <div
        onClick={() => onChange("isAgencyStaff", !identity.isAgencyStaff)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          border: `1.5px solid ${identity.isAgencyStaff ? BRAND : "#d9e3ee"}`,
          borderRadius: 7,
          padding: "10px 14px",
          background: identity.isAgencyStaff ? "#f0f5fb" : "#fafbfc",
          cursor: "pointer",
          transition: "all 0.18s ease",
          userSelect: "none",
        }}
      >
        <Checkbox
          checked={identity.isAgencyStaff}
          onChange={(e) => {
            e.stopPropagation();
            onChange("isAgencyStaff", e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ flexShrink: 0 }}
        />
        <div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: identity.isAgencyStaff ? BRAND : "#101828",
              transition: "color 0.15s ease",
            }}
          >
            Agency Staff
          </div>
          <div style={{ fontSize: 12, color: "#5B6472" }}>
            Employed through an external staffing agency.
          </div>
        </div>
      </div>
    </div>
  );
});

export const RoleStep = memo(({ selectedRole, onSelect, roles, isLoading }) => (
  <div
    style={{ border: "1px solid #edf2f9", borderRadius: 8, padding: "16px 20px" }}
  >
    <SectionLabel>Role Selection</SectionLabel>
    <Text style={{ fontSize: 12, color: "#5B6472", display: "block", marginBottom: 12 }}>
      Choose the role that best describes this staff member's responsibilities.
    </Text>
    {isLoading ? (
      <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
        <Spin size="small" />
      </div>
    ) : (
      <Row gutter={12}>
        {roles.map((role) => (
          <Col key={role.role_id} span={8}>
            <RoleCard
              role={{ title: role.role_name }}
              selected={selectedRole === role.role_id}
              onSelect={() => onSelect(role.role_id)}
            />
          </Col>
        ))}
      </Row>
    )}
  </div>
));

export const FacilityStep = memo(
  ({
    facilityList,
    allModules,
    isLoading,
    facilities,
    facilityModules,
    onToggleFacility,
    onToggleModule,
  }) => (
    <div
      style={{
        border: "1px solid #edf2f9",
        borderRadius: 8,
        padding: "16px 20px",
      }}
    >
      <SectionLabel>Facility and Module Access</SectionLabel>

      {isLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}
        >
          <Spin size="small" />
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 0,
              padding: "0 12px",
              marginBottom: 8,
            }}
          >
            <div style={{ width: 22, flexShrink: 0, marginRight: 10 }} />
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 11,
                fontWeight: 600,
                color: "#5B6472",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                marginRight: 12,
              }}
            >
              Facility
            </div>
            <div style={{ width: 1, marginRight: 12 }} />
            <div style={{ display: "flex", gap: 8 }}>
              {allModules.map((mod) => {
                const Icon = getModuleIcon(mod.name);
                return (
                  <Tooltip key={mod.module_id} title={mod.name} placement="top">
                    <div
                      style={{
                        width: 34,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon style={{ fontSize: 13, color: "#c8d6e5" }} />
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {facilityList.map((f) => (
            <FacilityRow
              key={f.facility_id}
              facility={f}
              facilitySelected={facilities[f.facility_id] || false}
              facilityMods={facilityModules[f.facility_id] || {}}
              allModules={allModules}
              onToggle={() => onToggleFacility(f.facility_id)}
              onToggleModule={(modKey) => onToggleModule(f.facility_id, modKey)}
            />
          ))}
        </>
      )}
    </div>
  )
);

/**
 * LoginStep — mode-aware login credentials form.
 *
 * mode="create"  Username + Password are always required. No toggle.
 * mode="edit"    Username pre-filled. Password hidden behind "Set Password"
 *                toggle (off by default). Email shown when isManager.
 */
export const LoginStep = memo(({ login, onChange, mode, isManager, isSupportStaff }) => {
  const [form] = Form.useForm();
  const isCreateMode = mode === "create";
  const requiresEmail = isManager || isSupportStaff;

  return (
    <div
      style={{ border: "1px solid #edf2f9", borderRadius: 8, padding: "16px 20px" }}
    >
      <SectionLabel>Login Setup</SectionLabel>
      <Text
        style={{ fontSize: 12, color: "#5B6472", display: "block", marginBottom: 14 }}
      >
        Configure login credentials for this staff member.
      </Text>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          username: login.username,
          password: login.password,
          email: login.email,
        }}
        onValuesChange={(changed) => {
          Object.keys(changed).forEach((field) => onChange(field, changed[field]));
        }}
      >
        {/* Username */}
        <Form.Item
          name="username"
          label="Username"
          validateTrigger="onBlur"
          normalize={USERNAME_NORMALIZE}
          rules={[
            { required: true, message: "Please enter the username." },
            ...USERNAME_RULES,
          ]}
        >
          <Input
            prefix={<UserOutlined style={{ color: "#c8d6e5" }} />}
            placeholder="e.g. sarahjohnson"
          />
        </Form.Item>

        {/* Email — visible when the selected role is Manager or Support Staff */}
        {requiresEmail && (
          <Form.Item
            name="email"
            label="Email"
            validateTrigger="onBlur"
            rules={[
              { required: true, message: "Please enter the email." },
              { type: "email", message: "Please enter a valid email address." },
            ]}
          >
            <Input
              prefix={<MailOutlined style={{ color: "#c8d6e5" }} />}
              placeholder="e.g. sarah.johnson@example.com"
            />
          </Form.Item>
        )}

        {isCreateMode ? (
          /* Create Mode — password field always visible and mandatory */
          <>
          <Form.Item
            name="password"
            label="Password"
            validateTrigger="onBlur"
            normalize={PASSWORD_NORMALIZE}
            extra={PASSWORD_HINT}
            rules={[
              { required: true, message: "Please enter the password." },
              ...PASSWORD_RULES,
            ]}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#c8d6e5" }} />}
              placeholder="Enter password"
            />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="Confirm Password"
            validateTrigger="onBlur"
            dependencies={["password"]}
            normalize={PASSWORD_NORMALIZE}
            rules={confirmPasswordRules("password")}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "#c8d6e5" }} />}
              placeholder="Confirm password"
            />
          </Form.Item>
          </>
        ) : (
          /* Edit Mode — optional "Set Password" toggle */
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: `1.5px solid ${login.resetPassword ? BRAND : "#d9e3ee"}`,
                borderRadius: 7,
                padding: "10px 14px",
                background: login.resetPassword ? "#f0f5fb" : "#fafbfc",
                marginBottom: login.resetPassword ? 12 : 0,
                cursor: "pointer",
                transition: "all 0.18s ease",
                userSelect: "none",
              }}
              onClick={() => {
                const next = !login.resetPassword;
                onChange("resetPassword", next);
                if (!next) {
                  form.setFieldsValue({ password: "", confirm_password: "" });
                  onChange("password", "");
                  onChange("confirm_password", "");
                }
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: login.resetPassword ? BRAND : "#101828",
                    marginBottom: 2,
                    transition: "color 0.15s ease",
                  }}
                >
                  Set Password
                </div>
                <div style={{ fontSize: 12, color: "#5B6472" }}>
                  Assign a temporary password for first login.
                </div>
              </div>
              <Switch
                checked={login.resetPassword}
                onChange={(checked) => {
                  onChange("resetPassword", checked);
                  if (!checked) {
                    form.setFieldsValue({ password: "", confirm_password: "" });
                    onChange("password", "");
                    onChange("confirm_password", "");
                  }
                }}
                onClick={(_, e) => e.stopPropagation()}
                style={{
                  flexShrink: 0,
                  background: login.resetPassword ? BRAND : undefined,
                }}
              />
            </div>

            {login.resetPassword && (
              <div
                style={{
                  opacity: 1,
                  transform: "translateY(0)",
                  transition: "opacity 0.2s ease, transform 0.2s ease",
                }}
              >
                <Form.Item
                  name="password"
                  label="Password"
                  validateTrigger="onBlur"
                  normalize={PASSWORD_NORMALIZE}
                  extra={PASSWORD_HINT}
                  rules={[
                    { required: true, message: "Please enter the password." },
                    ...PASSWORD_RULES,
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#c8d6e5" }} />}
                    placeholder="Enter password"
                  />
                </Form.Item>
                <Form.Item
                  name="confirm_password"
                  label="Confirm Password"
                  validateTrigger="onBlur"
                  dependencies={["password"]}
                  normalize={PASSWORD_NORMALIZE}
                  rules={confirmPasswordRules("password")}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: "#c8d6e5" }} />}
                    placeholder="Confirm password"
                  />
                </Form.Item>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
});

export const ComplianceStep = memo(({ compliance, onFileChange, onFieldChange }) => {
  const [activeKey, setActiveKey] = useState(COMPLIANCE_ITEMS[0].key);

  const [touchedMap, setTouchedMap] = useState({
    flu: {},
    covid: {},
    stat: {},
    police: {},
  });

  const markTouched = useCallback((key, field) => {
    setTouchedMap((prev) => ({
      ...prev,
      [key]: { ...prev[key], [field]: true },
    }));
  }, []);

  const getStatus = (key) => {
    if (key === "flu" || key === "covid") {
      const item = compliance[key];
      if (item.file === null) return "none";
      return item.vaccinationDate !== null ? "done" : "pending";
    }
    if (key === "stat") {
      return compliance.stat.file !== null ? "done" : "none";
    }
    if (key === "police") {
      const d = compliance.police;
      if (d.file === null) return "none";
      return d.referenceNumber.trim() !== "" &&
        d.certificateSighted !== null &&
        d.offences !== null &&
        d.issueDate !== null
        ? "done"
        : "pending";
    }
    return "none";
  };

  const activeItem = COMPLIANCE_ITEMS.find((i) => i.key === activeKey);

  return (
    <div
      style={{ border: "1px solid #edf2f9", borderRadius: 8, padding: "16px 20px" }}
    >
      <SectionLabel>Initial Compliance Setup</SectionLabel>
      <Text
        style={{ fontSize: 12, color: "#5B6472", display: "block", marginBottom: 14 }}
      >
        Select a compliance type to upload documents and complete the required
        details.
      </Text>

      <div style={{ display: "flex", gap: 0 }}>
        <div style={{ width: 172, flexShrink: 0, paddingRight: 14 }}>
          {COMPLIANCE_ITEMS.map((item) => (
            <ComplianceNavItem
              key={item.key}
              item={item}
              isActive={activeKey === item.key}
              status={getStatus(item.key)}
              onClick={() => setActiveKey(item.key)}
            />
          ))}
        </div>

        <div
          style={{
            width: 1,
            background: "#edf2f9",
            flexShrink: 0,
            alignSelf: "stretch",
          }}
        />

        <div style={{ flex: 1, minWidth: 0, paddingLeft: 16, minHeight: 200 }}>
          <div
            style={{
              marginBottom: 14,
              paddingBottom: 10,
              borderBottom: "1px solid #edf2f9",
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: "#101828" }}>
              {activeItem.label}
            </div>
            <div style={{ fontSize: 12, color: "#5B6472", marginTop: 2 }}>
              {activeItem.sub}
            </div>
          </div>

          <ComplianceDetailPanel
            item={activeItem}
            complianceData={compliance[activeKey]}
            touched={touchedMap[activeKey] || {}}
            onMarkTouched={(field) => markTouched(activeKey, field)}
            onFileChange={onFileChange}
            onFieldChange={onFieldChange}
          />
        </div>
      </div>
    </div>
  );
});

// ─── Animated step wrapper ─────────────────────────────────────────────────────

const TRANSITION_MS = 220;

export const AnimatedStep = ({ children, stepKey }) => {
  const [visible, setVisible] = useState(false);

  React.useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, [stepKey]);

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${TRANSITION_MS}ms ease, transform ${TRANSITION_MS}ms ease`,
      }}
    >
      {children}
    </div>
  );
};
